import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import SkeletonTitle from '@afosto/components-react/SkeletonTitle';
import { PageHeaderBase, PageHeaderRow } from '../PageHeader';
import { PageWrapper } from '../Page';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import MUIBreadcrumbs from '@material-ui/core/Breadcrumbs';
import Title from '@afosto/components-react/Title';
import clsx from 'clsx';
import Hidden from '@material-ui/core/Hidden';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Box from '@material-ui/core/Box';

export const PageBase = styled.div`
  width: 100%;
  margin: 0 auto;

  &:not(.full-width) {
    max-width: ${props => props.theme.pageWidth}px;
  }
`;

const SkeletonPage = ({
  title,
  titleHidden,
  primaryAction,
  subNavigation,
  breadcrumbs,
  fullWidth,
  children,
  style,
}) => {
  const skeletonBreadcrumbs = Array.from(Array(breadcrumbs).keys()).map(i => (
    <div
      key={i}
      style={{
        width: `${Math.round(Math.random() * 40 + 40)}px`,
        paddingBottom: '6px',
        paddingTop: '6px',
      }}
    >
      <SkeletonBody key={i} lines={1} />
    </div>
  ));

  const skeletonBreadcrumbsMarkup = (
    <MUIBreadcrumbs separator="">{skeletonBreadcrumbs}</MUIBreadcrumbs>
  );

  const skeletonSubNavigation = Array.from(Array(subNavigation).keys()).map(i => (
    <div
      key={i}
      style={{
        width: `${Math.round(Math.random() * 40 + 40)}px`,
        paddingTop: '12px',
        paddingBottom: '8px',
      }}
    >
      <SkeletonBody key={i} lines={1} />
    </div>
  ));

  const skeletonSubNavigationMarkup = (
    <MUIBreadcrumbs separator="" style={{ marginBottom: '2px' }}>
      {skeletonSubNavigation}
    </MUIBreadcrumbs>
  );

  const skeletonPrimaryActionMarkup = (
    <div style={{ marginTop: '2px', marginBottom: '2px' }}>
      <SkeletonTitle />
    </div>
  );

  return (
    <>
      <Hidden implementation="js" smDown>
        <PageBase className={clsx({ 'full-width': fullWidth })} style={style}>
          {primaryAction ? (
            <PageHeaderBase>
              <PageHeaderRow>
                <div>
                  {breadcrumbs && skeletonBreadcrumbsMarkup}
                  {titleHidden ? null : (title && <Title title={title} />) || <SkeletonTitle />}
                </div>
                {skeletonPrimaryActionMarkup}
              </PageHeaderRow>
              {subNavigation && skeletonSubNavigationMarkup}
            </PageHeaderBase>
          ) : (
            <PageHeaderBase>
              {breadcrumbs && skeletonBreadcrumbsMarkup}
              {titleHidden ? null : (title && <Title title={title} />) || <SkeletonTitle />}
              {subNavigation && skeletonSubNavigationMarkup}
            </PageHeaderBase>
          )}
          <PageWrapper>{children}</PageWrapper>
        </PageBase>
      </Hidden>
      <Hidden implementation="js" mdUp>
        <PageBase className={clsx({ 'full-width': fullWidth })}>
          <AppBar color="inherit" position="sticky" elevation={1}>
            <Toolbar>
              <PageHeaderRow>
                {(title && <Title disableMinHeight title={title} variant="h6" />) || (
                  <SkeletonTitle />
                )}
              </PageHeaderRow>
            </Toolbar>
            {subNavigation && (
              <Box pr={1} pl={1} pb={1}>
                {skeletonSubNavigationMarkup}
              </Box>
            )}
          </AppBar>
          <PageWrapper>{children}</PageWrapper>
        </PageBase>
      </Hidden>
    </>
  );
};

SkeletonPage.propTypes = {
  title: PropTypes.string,
  titleHidden: PropTypes.bool,
  primaryAction: PropTypes.bool,
  subNavigation: PropTypes.number,
  breadcrumbs: PropTypes.number,
  fullWidth: PropTypes.bool,
  children: PropTypes.node,
  style: PropTypes.object,
};

SkeletonPage.defaultProps = {
  title: null,
  titleHidden: false,
  primaryAction: false,
  subNavigation: null,
  breadcrumbs: null,
  fullWidth: false,
  children: undefined,
  style: undefined,
};

export default SkeletonPage;
